import { v7 as uuidv7 } from 'uuid';
import type { QueryClient } from '@tanstack/react-query';
import { LiveValue, type HyloServices, type Id, type SessionState, type Update } from '@/model';
import { i18next } from '@/i18n';
import { BrowserMedia } from './browser-media';
import { createCallApi } from './call-api';
import { CallEngine, callErrorKey } from './call-engine';
import { CallAttention } from './call-attention';
import { decodeCall, decodeSignal, isLiveCall, isParty, metadata, object, peerId, type CallApi, type CallMetadata, type CallWire } from './call-wire';

interface RuntimeState { engine: CallEngine | null; recovered: readonly CallMetadata[]; errorKey: string | null }
interface Host { engine(engine: CallEngine | null): void }
export class CallRuntime {
  readonly state = new LiveValue<RuntimeState>({engine:null,recovered:[],errorKey:null});
  private hosts = new Map<symbol,Host>();
  private unsubscribe: Array<() => void> = [];
  private engineSubscription?: () => void;
  private auth: SessionState | undefined;
  private account: Id | null = null;
  private epoch = 0;
  private syncGeneration = 0;
  private pts = -1;
  private read?: Promise<CallMetadata[]>;
  private query?: QueryClient;
  private controller = new AbortController();
  private calls = new Map<Id,CallMetadata>();
  private terminal = new Set<Id>();
  private localCompleted = new Set<Id>();
  private invitations = new Map<Id,CallWire>();
  private versions = new Map<Id,number>();
  private resyncPending = false;
  private activity = false;
  private attempt?: {conversationId:Id;kind:'voice'|'video';clientId:Id};
  private attention?: CallAttention;
  private dismissTimer?: ReturnType<typeof setTimeout>;
  constructor(private readonly services: HyloServices) {}
  get accountId(): Id | null { return this.account; }
  get hostCount(): number { return this.hosts.size; }
  detailKey(id: Id) { return [...this.prefix(),'detail',id,this.syncGeneration]; }

  attach(host: Host, client: QueryClient): () => void {
    const key = Symbol('calls-host'); this.hosts.set(key,host); this.query = client;
    if (!this.unsubscribe.length) this.connect();
    if (this.hosts.keys().next().value === key) host.engine(this.state.get().engine);
    return () => {
      const primary = this.hosts.keys().next().value === key;
      if (primary) host.engine(null);
      this.hosts.delete(key);
      if (primary) this.primary()?.engine(this.state.get().engine);
      queueMicrotask(() => { if (!this.hosts.size) this.disconnect(); });
    };
  }
  private primary(): Host | undefined { return this.hosts.values().next().value; }
  private connect(): void {
    this.unsubscribe = [this.services.session.state.subscribe(() => this.sessionChanged()),this.services.sync.onUpdate(['call.updated','call.signal'],this.update)];
    let previous = this.services.sync.connection.get().phase;
    this.unsubscribe.push(this.services.sync.connection.subscribe(() => {
      const phase = this.services.sync.connection.get().phase;
      if (phase === 'resyncing' && previous !== 'resyncing') this.rebaseSync();
      if (phase === 'online' && previous !== 'online') { this.resyncPending = false; void this.recover(); }
      previous = phase;
    }));
    document.addEventListener('visibilitychange',this.visibility);
    window.addEventListener('pagehide',this.pagehide);
    this.sessionChanged();
  }
  private sessionChanged(): void {
    const auth = this.services.session.state.get();
    if (this.auth === auth) return;
    this.retire(); this.auth = auth;
    this.account = auth.phase === 'signedIn' ? auth.accountId : null;
    if (this.account && this.services.sync.connection.get().phase === 'online') void this.recover();
  }
  private retire(): void {
    if (this.account) this.query?.removeQueries({queryKey:this.prefix()});
    this.epoch++; this.controller.abort(); this.controller = new AbortController(); this.read = undefined;
    this.engineSubscription?.(); this.engineSubscription = undefined;
    this.state.get().engine?.dispose(); this.attention?.stop(); this.attention = undefined;
    clearTimeout(this.dismissTimer); this.endActivity();
    this.calls.clear(); this.terminal.clear(); this.localCompleted.clear(); this.invitations.clear(); this.versions.clear(); this.pts = -1; this.attempt = undefined;
    this.state.set({engine:null,recovered:[],errorKey:null}); this.primary()?.engine(null);
  }
  private rebaseSync(): void {
    // Core fences old stream delivery before publishing this phase. Its next generation can start at a lower pts.
    this.syncGeneration++; this.controller.abort(); this.controller = new AbortController(); this.read = undefined;
    this.pts = -1; this.versions.clear(); this.terminal.clear(); this.calls.clear(); this.invitations.clear();
    this.query?.removeQueries({queryKey:this.prefix()});
    this.state.set({...this.state.get(),recovered:[],errorKey:null});
  }
  private disconnect(): void {
    // A route fallback disappearing cannot keep a microphone alive. Never rebind its cleanup to another login.
    this.state.get().engine?.abandon();
    this.retire(); this.auth = undefined; this.account = null;
    for (const unsubscribe of this.unsubscribe) unsubscribe(); this.unsubscribe = [];
    document.removeEventListener('visibilitychange',this.visibility); window.removeEventListener('pagehide',this.pagehide);
  }
  private visibility = () => {
    this.state.get().engine?.setForeground(document.visibilityState !== 'hidden');
    if (document.visibilityState === 'visible') void this.recover();
  };
  private pagehide = () => { this.state.get().engine?.abandon(); };
  private valid(epoch: number): boolean {
    const auth = this.services.session.state.get();
    return epoch === this.epoch && auth.phase === 'signedIn' && auth.accountId === this.account;
  }
  private api(): CallApi {
    const api = createCallApi(this.services.http), epoch = this.epoch;
    const guarded = async <T,>(work: () => Promise<T>): Promise<T> => {
      if (!this.valid(epoch)) throw new DOMException('login_retired','AbortError');
      const value = await work();
      if (!this.valid(epoch)) throw new DOMException('login_retired','AbortError');
      return value;
    };
    return {
      start: input => guarded(() => api.start(input)), active: signal => guarded(() => api.active(signal)), get: (id,signal) => guarded(() => api.get(id,signal)),
      accept: id => guarded(() => api.accept(id)), decline: id => guarded(() => api.decline(id)), cancel: id => guarded(() => api.cancel(id)), end: id => guarded(() => api.end(id)),
      signal: (id,type,payload) => guarded(() => api.signal(id,type,payload)),
    };
  }
  private prefix() { const config = this.services.flags.config().get(); const realm = object(config?.realm); return ['hylo',location.origin,typeof realm?.id === 'string' ? realm.id : null,this.account,'calls'] as const; }
  private publish(call: CallMetadata): CallMetadata {
    const safe = metadata(call); this.calls.set(safe.id,safe);
    if (!isLiveCall(safe)) { this.terminal.add(safe.id); if (this.terminal.size > 256) this.terminal.delete(this.terminal.values().next().value!); }
    if (this.calls.size > 256) this.calls.delete(this.calls.keys().next().value!);
    this.query?.setQueryData(this.detailKey(safe.id),safe);
    this.state.set({...this.state.get(),recovered:[...this.calls.values()].filter(isLiveCall)});
    return safe;
  }
  private apply(call: CallWire): void {
    if (!this.account || !isParty(call,this.account)) return;
    if (isLiveCall(call) && this.terminal.has(call.id)) return;
    if (this.calls.get(call.id)?.state === 'active' && call.state === 'ringing') return;
    this.publish(call);
    if (call.state !== 'ringing') this.invitations.delete(call.id);
    const engine = this.state.get().engine;
    if (engine && !engine.ended) {
      if (call.state === 'ringing' && call.callee_id === this.account && engine.state.get().call?.id !== call.id) {
        // Invitations belong to the account, including while a different call is still acquiring/creating.
        this.invitations.set(call.id,call);
        if (this.invitations.size > 16) this.invitations.delete(this.invitations.keys().next().value!);
      } else engine.event(call);
      return;
    }
    this.invitations.delete(call.id);
    if (call.state === 'ringing' && call.callee_id === this.account && !this.localCompleted.has(call.id) && Date.now() - Date.parse(call.created_at) < 60_000) {
      const next = this.adopt('incoming',call.conversation_id,call.kind === 'video' ? 'video' : 'voice');
      next.update(call);
      const peer = this.services.people.account(call.caller_id).get();
      const name = peer?.display_name || i18next.t('calls:state.someone');
      const title = i18next.t(call.kind === 'video' ? 'calls:attention.video' : 'calls:attention.voice',{name});
      this.attention = new CallAttention(title);
    }
  }
  private update = (update: Update) => {
    if (!this.account || !Number.isSafeInteger(update.pts) || update.pts <= this.pts) return;
    this.pts = update.pts;
    try {
      if (update.type === 'call.updated') {
        const call = decodeCall(update.data.call); this.versions.set(call.id,update.pts); this.apply(call);
      } else if (update.type === 'call.signal') {
        const signal = decodeSignal(update.data);
        if (signal) this.state.get().engine?.event(signal);
      }
    } catch {
      if (!this.resyncPending) { this.resyncPending = true; void this.services.sync.resync().catch(() => undefined); }
    }
  };
  async recover(): Promise<CallMetadata[]> {
    if (!this.account || this.services.sync.connection.get().phase !== 'online') return [];
    if (this.read) return this.read;
    const epoch = this.epoch, generation = this.syncGeneration, beforePts = this.pts, api = this.api(), signal = this.controller.signal;
    const valid = () => this.valid(epoch) && generation === this.syncGeneration;
    const load = async () => {
      const calls = await api.active(signal);
      if (!valid()) return [];
      for (const call of calls) if ((this.versions.get(call.id) ?? -1) <= beforePts) this.apply(call);
      for (const [id,call] of this.calls) {
        if (isLiveCall(call)&&!calls.some(next=>next.id===id)&&(this.versions.get(id)??-1)<=beforePts) this.calls.delete(id);
      }
      const current = this.state.get().engine?.state.get().call;
      if (current && !this.state.get().engine?.ended && !calls.some(c => c.id === current.id)) {
        const final = await api.get(current.id,signal);
        if (!valid()) return [];
        if ((this.versions.get(final.id) ?? -1) <= beforePts) this.apply(final);
      }
      const recovered=[...this.calls.values()].filter(isLiveCall);
      this.state.set({...this.state.get(),recovered,errorKey:null});
      return recovered;
    };
    const read = this.query ? this.query.fetchQuery({queryKey:[...this.prefix(),'active',generation],queryFn:load,retry:false,staleTime:0,gcTime:0}) : load();
    this.read = read;
    try { return await read; }
    catch (error) { if (valid()) this.state.set({...this.state.get(),errorKey:callErrorKey(error)}); return []; }
    finally { if (this.read === read) this.read = undefined; }
  }
  async detail(id: Id, signal?: AbortSignal): Promise<CallMetadata> {
    const epoch = this.epoch, generation = this.syncGeneration, pts = this.pts;
    const abort = signal ? AbortSignal.any([signal,this.controller.signal]) : this.controller.signal;
    const load = async () => {
      const call = await this.api().get(id,abort);
      if (!this.valid(epoch) || generation !== this.syncGeneration || !this.account || !isParty(call,this.account)) throw new DOMException('call_read_retired','AbortError');
      if ((this.versions.get(id) ?? -1) <= pts) this.apply(call);
      return this.calls.get(id) ?? metadata(call);
    };
    // The routed useQuery owns this fetch; nesting fetchQuery with the same key would await itself.
    return load();
  }
  async endRecovered(id: Id): Promise<void> { this.apply(await this.api().end(id)); }

  async start(conversationId: Id, kind: 'voice'|'video', signal?: AbortSignal): Promise<void> {
    if (signal?.aborted) return;
    if (!this.account || this.services.sync.connection.get().phase !== 'online') { this.state.set({...this.state.get(),errorKey:'error.offline'}); return; }
    if (this.state.get().engine && !this.state.get().engine!.ended) { this.focus(); return; }
    if (this.state.get().recovered.length) { this.state.set({...this.state.get(),errorKey:'state.busy'}); return; }
    const previous=this.state.get().engine?.state.get();
    const retry=previous?.endReason==='start'&&previous.errorKey==='error.connection'&&this.attempt?.conversationId===conversationId&&this.attempt.kind===kind;
    const clientId=retry?this.attempt!.clientId:uuidv7();
    this.attempt={conversationId,kind,clientId};
    const engine = this.adopt('outgoing',conversationId,kind);
    const cancel = () => { void engine.hangUp(); };
    signal?.addEventListener('abort',cancel,{once:true});
    try { await engine.start(clientId); }
    finally { signal?.removeEventListener('abort',cancel); }
  }
  private adopt(direction: 'incoming'|'outgoing', conversationId: Id, kind: 'voice'|'video'): CallEngine {
    this.engineSubscription?.(); clearTimeout(this.dismissTimer); this.attention?.stop(); this.attention = undefined;
    const previous = this.state.get().engine;
    // Finished media is already closed. Keep its late-create cleanup alive within this guarded account session.
    if (previous && !previous.ended) previous.dispose();
    this.endActivity();
    const engine = new CallEngine({me:this.account!,direction,conversationId,kind,api:this.api(),media:new BrowserMedia(),canSignal:()=>this.services.sync.connection.get().phase==='online'});
    engine.setForeground(document.visibilityState !== 'hidden');
    this.activity = true; window.dispatchEvent(new Event('hylo:call-started'));
    this.state.set({...this.state.get(),engine,errorKey:null});
    this.engineSubscription = engine.state.subscribe(() => {
      if (engine !== this.state.get().engine) return;
      const state = engine.state.get();
      if (state.phase !== 'incoming') { this.attention?.stop(); this.attention = undefined; }
      if (engine.ended) {
        this.endActivity();
        if (state.call) {
          // Closing local media cannot finalize server state: a failed /end must remain recoverable.
          this.localCompleted.add(state.call.id);
          if (this.localCompleted.size > 256) this.localCompleted.delete(this.localCompleted.values().next().value!);
          if (!this.terminal.has(state.call.id)) this.publish(state.call);
        }
        clearTimeout(this.dismissTimer);
        if (!state.errorKey) this.dismissTimer = setTimeout(() => this.dismiss(engine),2000);
        const epoch = this.epoch;
        queueMicrotask(() => {
          if (!this.valid(epoch) || this.state.get().engine !== engine) return;
          for (const invitation of this.invitations.values()) {
            this.apply(invitation);
            if (!this.state.get().engine?.ended) break;
          }
        });
      }
    });
    this.primary()?.engine(engine); return engine;
  }
  private endActivity(): void { if (this.activity) { this.activity = false; window.dispatchEvent(new Event('hylo:call-ended')); } }
  dismiss(engine = this.state.get().engine): void {
    if (!engine || engine !== this.state.get().engine || !engine.ended) return;
    clearTimeout(this.dismissTimer); this.engineSubscription?.(); this.engineSubscription = undefined;
    this.state.set({...this.state.get(),engine:null}); this.primary()?.engine(null);
  }
  focus(): void { this.primary()?.engine(this.state.get().engine); }
  peer(): Id | null { const call = this.state.get().engine?.state.get().call; return call && this.account ? peerId(call,this.account) : null; }
}

const runtimes = new WeakMap<HyloServices,CallRuntime>();
export function callRuntime(services: HyloServices): CallRuntime {
  let runtime = runtimes.get(services); if (!runtime) { runtime = new CallRuntime(services); runtimes.set(services,runtime); } return runtime;
}
